import { call, put, takeEvery } from "redux-saga/effects"
import {
  AUTHENTICATION_TOKEN,
  CURRENT_FLEET,
  CURRENT_FLEET_ID,
  ENGINES,
  MAKES,
  MODELS,
  POST,
  PUT,
  YEARS
} from "../../constants/application"
import { ERROR, FAILURE, SUCCESS } from "../../constants/notification"
import * as apiErrorHelpers from "../../helpers/apiErrorHelpers"
import * as sessionHelpers from "../../helpers/sessionHelpers"
import api from "../../utilities/api"
import i18n from "../../utilities/i18n"
import { gaJSON, trackFirebaseAnalytics } from "../../helpers/googleAnalyticsHelpers"
import { getVehicleProps } from "../../helpers/segmentHelpers"
import { DEACTIVE, MANUALLY, NO } from "../../constants/vehicles"

const VEHICLE_MAINTENANCE_SCHEDULES = "/services/recommended"
const LOAD_ALL_VEHICLES_URL = "/vehicles"
const LOAD_VEHICLE_STATE_SUMMARY = "/fleets/vehicles_state_summary"
const VEHICLES_BULK_UPSERTIONS = "/vehicles/bulk_upsertions"
const SERVICES_URL = "/services"
const LOAD_BASIC_VEHICLE_DETAILS_URL = "/vehicles/basic_details"
const TRANSFER_VEHICLES_URL = "/vehicles/transfer_vehicles"
const SAVE_DOCUMENTS_URL = "/glovebox_items"
const BULK_VEHICLE_URL = "/fleet/vehicles"
const VEHICLE_REASSIGNMENT_URL = "/fleet/vehicles/vehicle_reassignment"

export function* createOrUpdateVehicle(action) {
  try {
    const { formData } = action.payload
    yield put({ type: "VEHICLE_CREATE_OR_UPDATE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: "/vehicles/" + (formData.id ? formData.id : ""),
      body: { vehicle: formData }
    }

    const response = yield call(
      formData.id ? api.utility.put : api.utility.post,
      api.path(request.url),
      { body: request.body }
    )

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: formData.id ? "VEHICLE_UPDATE_SUCCEEDED" : "VEHICLE_CREATE_SUCCEEDED",
      payload: {
        vehicle: response.body.vehicle
      }
    })

    const afterRequestData = {
      vehicle: response.body.vehicle
    }

    trackFirebaseAnalytics({
      event: !formData.id ? gaJSON.vehicle_added : gaJSON.vehicle_updated,
      params: {
        ...getVehicleProps(response.body.vehicle, { shouldAddCarsAdded: !formData.id }),
        methodToAddVehicle: MANUALLY,
        duringOnboarding: NO
      }
    })

    if (action.callback) yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_CREATE_OR_UPDATE_FAILED" })

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    if (action.callback) yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* createVehicle

export function* createOrUpdateVehicleV6(action) {
  try {
    const { formData } = action.payload
    yield put({ type: "VEHICLE_CREATE_OR_UPDATE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: "/fleet/vehicles/" + (formData.id ? formData.id : ""),
      body: { vehicles: formData }
    }

    const response = yield call(
      formData.id ? api.utility.patch : api.utility.post,
      api.pathV6(request.url),
      { body: request.body }
    )

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: formData.id ? "VEHICLE_UPDATE_SUCCEEDED" : "VEHICLE_CREATE_SUCCEEDED",
      payload: {
        vehicle: response.body
      }
    })

    const afterRequestData = {
      vehicle: response.body
    }

    trackFirebaseAnalytics({
      event: !formData.id ? gaJSON.vehicle_added : gaJSON.vehicle_updated,
      params: {
        ...getVehicleProps(response.body, { shouldAddCarsAdded: !formData.id }),
        methodToAddVehicle: MANUALLY,
        duringOnboarding: NO
      }
    })

    if (action.callback) yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_CREATE_OR_UPDATE_FAILED" })

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    if (action.callback) yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* createVehicle

export function* bulkUpsertVehicles(action) {
  try {
    yield put({ type: "BULK_UPSERT_VEHICLES_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: VEHICLES_BULK_UPSERTIONS,
      body: { fileData: action.payload.file }
    }

    const response = yield call(api.utility.post, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "BULK_UPSERT_VEHICLES_SUCCEEDED",
      payload: {
        success: response.success
      }
    })

    const afterRequestData = {
      alertMessage: action.payload.t("bulkUploadSuccessLabel"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "BULK_UPSERT_VEHICLES_FAILED" })

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors),
      alertMessage: action.payload.t("bulkUploadFailureLabel"),
      alertType: ERROR
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* bulkUpsertVehicles

export function* deleteVehicle(action) {
  try {
    yield put({ type: "VEHICLE_DELETE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const vehicleId = action.payload.vehicleId

    const request = {
      url: `/vehicles/${vehicleId}`,
      body: {}
    }

    const response = yield call(api.utility.del, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_DELETE_SUCCEEDED",
      payload: {
        vehicle: response.body
      }
    })

    const afterRequestData = {
      alertMessage: i18n.t("vehicle:vehicleDeleted"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_DELETE_FAILED" })

    const afterRequestData = {
      alertMessage: apiErrorHelpers.toString(errors),
      alertType: ERROR
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* deleteVehicle

export function* deactivateVehicle(action) {
  try {
    yield put({ type: "VEHICLE_DEACTIVATE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const vehicleId = action.payload.vehicleId

    const request = {
      url: `/vehicles/${vehicleId}/deactivate`,
      body: {}
    }

    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_DEACTIVATE_SUCCEEDED",
      payload: {
        vehicle: response.body
      }
    })

    const afterRequestData = {
      alertMessage: i18n.t("vehicleDeactivate"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_DEACTIVATE_FAILED" })

    const afterRequestData = {
      alertMessage: apiErrorHelpers.toString(errors),
      alertType: ERROR
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* deleteVehicle

export function* loadVehicle(action) {
  try {
    const defaultOptions = {
      dataType: "standard"
    }

    const payloadOptions = action.payload.options || {}

    const options = {
      ...defaultOptions,
      ...payloadOptions
    }

    yield put({ type: "SET_LOADING" })
    yield put({ type: "UNSET_LOADING_ERROR" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    ////////////////////////////
    //   LOAD VEHICLE DATA    //
    ////////////////////////////
    yield put({ type: "VEHICLE_LOAD_REQUESTED" })

    const vehicleId = action.payload.vehicleId
    const search = action.payload.options.search
    let body = {}
    if (search) {
      body.search = true
    }

    let url = `/vehicles/${vehicleId}`
    const vehicleRequest = {
      url: url,
      body: body
    }

    if (options["dataType"] === "extended") {
      yield put({ type: "MAINTENANCE_INTERVAL_SAGA", payload: { vehicleIds: [vehicleId] } })
      vehicleRequest.body.include = "all"
    }

    const vehicleResponse = yield call(api.utility.get, api.path(vehicleRequest.url), {
      body: vehicleRequest.body
    })

    apiErrorHelpers.catchErrors(vehicleResponse.body)

    yield put({
      type: "VEHICLE_LOAD_SUCCEEDED",
      payload: {
        vehicle: vehicleResponse.body
      }
    })

    yield put({
      type: "SET_VEHICLE_DATA_TYPE",
      payload: {
        dataType: options["dataType"]
      }
    })

    if (search && action.callback) {
      yield call(action.callback, vehicleResponse.body)
    }

    if (!search) {
      ////////////////////////////

      ////////////////////////////
      //   LOAD VEHICLES DATA   //
      ////////////////////////////

      yield put({ type: "VEHICLES_LOAD_REQUESTED" })

      const vehiclesRequest = {
        url: LOAD_ALL_VEHICLES_URL,
        body: {}
      }

      const vehiclesResponse = yield call(api.utility.get, api.path(vehiclesRequest.url), {
        body: vehiclesRequest.body
      })

      apiErrorHelpers.catchErrors(vehiclesResponse.body)

      yield put({
        type: "VEHICLES_LOAD_SUCCEEDED",
        payload: {
          vehicles: vehiclesResponse.body
        }
      })

      yield put({
        type: "SET_VEHICLES_DATA_TYPE",
        payload: { dataType: "standard" }
      })
      ////////////////////////////

      ////////////////////////////
      //    LOAD USERS DATA     //
      ////////////////////////////
      // yield put({ type: "USERS_LOAD_REQUESTED" })
      //
      // const usersRequest = {
      //   url: "/fleet_users",
      //   body: {}
      // }
      //
      // const usersResponse = yield call(api.utility.get, api.path(usersRequest.url), {
      //   body: usersRequest.body
      // })
      //
      // apiErrorHelpers.catchErrors(usersResponse.body)
      //
      // yield put({
      //   type: "USERS_LOAD_SUCCEEDED",
      //   payload: {
      //     users: usersResponse.body
      //   }
      // })
      ////////////////////////////

      ////////////////////////////
      //   LOAD SERVICES DATA   //
      ////////////////////////////
      yield put({ type: "SERVICES_LOAD_REQUESTED" })

      const servicesRequest = {
        url: SERVICES_URL,
        body: { basic: true }
      }

      const servicesResponse = yield call(api.utility.get, api.path(servicesRequest.url), {
        body: servicesRequest.body
      })

      apiErrorHelpers.catchErrors(servicesResponse.body)

      yield put({
        type: "SERVICES_LOAD_SUCCEEDED",
        payload: {
          services: servicesResponse.body
        }
      })
      ////////////////////////////
    }
    yield put({ type: "UNSET_LOADING" })
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_LOAD_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    apiErrorHelpers.consoleLog(errors)
  }
} // function* loadVehicle

export function* loadDropdown(type, getUrl, action) {
  yield put({ type: `${type.toUpperCase()}_LOAD_REQUESTED` })

  api.setAuthApi({
    Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
  })

  try {
    const vehiclesResponse = yield call(api.utility.get, api.path(getUrl(action)), {})

    apiErrorHelpers.catchErrors(vehiclesResponse.body)

    yield put({
      type: `${type.toUpperCase()}_LOAD_SUCCEEDED`,
      payload: {
        [type]: vehiclesResponse.body // Got lucky, no fancy plurals used here.
      }
    })
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({
      type: `${type.toUpperCase()}_LOAD_SUCCEEDED`,
      payload: {
        [type]: []
      }
    })
    apiErrorHelpers.consoleLog(errors)
  }
}

function getMakesUrl(action) {
  return "/vehicles/years/" + action.payload.yearId + "/makes"
}

function getModelsUrl(action) {
  const { yearId, makeId } = action.payload
  return "/vehicles/years/" + yearId + "/makes/" + makeId + "/models"
}

function getEnginesUrl(action) {
  const { yearId, makeId, modelId, subModelId } = action.payload
  return (
    "/vehicles/years/" +
    yearId +
    "/makes/" +
    makeId +
    "/models/" +
    modelId +
    "/submodels/" +
    subModelId
  )
}

function getYearsUrl(action) {
  return "/vehicles/years"
}

export function* loadVehicles(action) {
  try {
    const authToken = sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    const payload = action.payload || {}
    const payloadOptions = payload.options || {}
    api.setAuthApi({
      Authorization: authToken
    })

    ////////////////////////
    // LOAD VEHICLES DATA //
    ////////////////////////
    let body = {}
    if (payloadOptions.fleet_id) {
      body.fleet_id = payloadOptions.fleet_id
      body.from = "fleet_web"
      body.page = payload.page
      body.query = payload.query
      body.search_type = payload.search_type
      body.is_active = payload.is_active
      body.order_column = payload.order_column
      body.order_direction = payload.order_direction
    }
    if (payload.fmc) {
      body.fmc = payload.fmc
      body.page = payload.page
      body.query = payload.query
      body.search_type = payload.search_type
      body.is_active = payload.is_active
      body.order_column = payload.order_column
      body.order_direction = payload.order_direction
    }
    const current_user_fleet_id = sessionHelpers.getApplicationCookie(CURRENT_FLEET_ID)
    const vehiclesRequest = {
      url: LOAD_ALL_VEHICLES_URL,
      body: body
    }

    if (action.payload && action.payload.download) {
      const url = `/fleets/${current_user_fleet_id}/vehicles.csv?auth_token=${authToken}&lancode=${
        action.payload.language && action.payload.language.toUpperCase()
      }&isDomainShellCanada=${action.payload.isDomainShellCanada}`
      window.open(api.pathV6(url))
      return
    }

    yield put({ type: "SET_VEHICLE_LOADING" })
    yield put({ type: "SET_LOADING" })
    yield put({ type: "UNSET_LOADING_ERROR" })
    yield put({ type: "VEHICLES_LOAD_REQUESTED" })

    if (action.payload && action.payload.addMaintenance) {
      vehiclesRequest.body.request = "active_order"
    }

    let vehiclesResponse
    if (payload.fmc || (payload.options && payload.options.isFleetCard)) {
      vehiclesResponse = yield call(api.utility.get, api.pathV6(vehiclesRequest.url), {
        body: vehiclesRequest.body
      })
    } else {
      vehiclesResponse = yield call(api.utility.get, api.path(vehiclesRequest.url), {
        body: vehiclesRequest.body
      })
    }

    apiErrorHelpers.catchErrors(vehiclesResponse.body)

    if (payload.pagination) {
      yield put({
        type: "VEHICLES_LOAD_PAGINATED_SUCCEEDED",
        payload: {
          vehicles: vehiclesResponse.body.vehicles,
          page: payload.page,
          total_pages: vehiclesResponse?.body?.total_pages,
          total_vehicle_count: vehiclesResponse?.body?.total_vehicle_count
        }
      })
    } else {
      yield put({
        type: "VEHICLES_LOAD_SUCCEEDED",
        payload: {
          vehicles: vehiclesResponse.body.vehicles ?? vehiclesResponse.body,
          section: payloadOptions.section,
          page: payload.page,
          total_pages: vehiclesResponse?.body?.total_pages,
          total_vehicle_count: vehiclesResponse?.body?.total_vehicle_count
        }
      })
    }
    if (action.callback) {
      yield call(action.callback, vehiclesResponse.body)
    }

    yield put({
      type: "SET_VEHICLES_DATA_TYPE",
      payload: { dataType: "standard" }
    })
    ////////////////////////

    ////////////////////////
    //  LOAD USERS DATA   //
    ////////////////////////
    // yield put({ type: "USERS_LOAD_REQUESTED" })
    //
    // const usersRequest = {
    //   url: "/fleet_users",
    //   body: {}
    // }
    //
    // const usersResponse = yield call(api.utility.get, api.path(usersRequest.url), {
    //   body: usersRequest.body
    // })
    //
    // apiErrorHelpers.catchErrors(usersResponse.body)
    //
    // yield put({
    //   type: "USERS_LOAD_SUCCEEDED",
    //   payload: {
    //     users: usersResponse.body
    //   }
    // })
    ////////////////////////

    yield put({ type: "UNSET_VEHICLE_LOADING" })
    yield put({ type: "UNSET_LOADING" })
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "LOADING_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    apiErrorHelpers.consoleLog(errors)
  }
} // function* loadVehicles

export function* loadBasicVehicles(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })
    const vehiclesRequest = {
      url: LOAD_BASIC_VEHICLE_DETAILS_URL
    }
    yield put({ type: "SET_LOADING" })
    yield put({ type: "UNSET_LOADING_ERROR" })
    yield put({ type: "SET_VEHICLES_BASIC_LOAD_REQUESTED" })

    const vehiclesResponse = yield call(api.utility.get, api.pathV6(vehiclesRequest.url), {
      body: vehiclesRequest.body
    })

    apiErrorHelpers.catchErrors(vehiclesResponse.body)

    yield put({
      type: "VEHICLES_BASIC_LOAD_SUCCEEDED",
      payload: {
        basicVehicles: vehiclesResponse.body ?? []
      }
    })
    if (action.callback) {
      yield call(action.callback, vehiclesResponse.body ?? [])
    }

    yield put({ type: "UNSET_VEHICLE_BASIC_LOADING" })
    yield put({ type: "UNSET_LOADING" })
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "LOADING_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    apiErrorHelpers.consoleLog(errors)
  }
} // function* loadBasicVehicles

export function* updateVehicle(action) {
  try {
    yield put({ type: "VEHICLE_UPDATE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const vehicleId = action.payload.formData.id

    const request = {
      url: `/vehicles/${vehicleId}`,
      body: {
        vehicle: {
          ...action.payload.formData
        }
      }
    }

    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_UPDATE_SUCCEEDED",
      payload: {
        vehicle: response.body.vehicle
      }
    })

    const afterRequestData = {
      alertMessage: i18n.t("vehicle:vehicleUpdated"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_UPDATE_FAILED" })

    const afterRequestData = {
      alertMessage: apiErrorHelpers.toString(errors),
      alertType: FAILURE
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* updateVehicle

export function* fetchVehicleServices(action) {
  try {
    yield put({ type: "VEHICLE_SERVICE_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const { vehicleId, page } = action.payload

    const request = {
      url: `/vehicles/${vehicleId}/show_services?page=${page}`
    }

    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_SERVICES_SUCCEEDED",
      payload: {
        vehicleServices: response.body
      }
    })

    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_SERVICES_FAILED" })

    const afterRequestData = {
      alertMessage: apiErrorHelpers.toString(errors),
      alertType: FAILURE
    }

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* transferVehicles(action) {
  try {
    yield put({ type: "VEHICLE_TRANSFER_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })
    const vehicle_ids = action.payload.options.vehicle_ids
    const old_fleet_ids = action.payload.options.old_fleet_ids
    const new_fleet_id = action.payload.options.new_fleet_id

    const request = {
      url: TRANSFER_VEHICLES_URL,
      body: {
        vehicle_ids: vehicle_ids,
        old_fleet_ids: old_fleet_ids,
        new_fleet_id: new_fleet_id
      }
    }

    const response = yield call(api.utility.post, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    const afterRequestData = {
      alertMessage: i18n.t("vehicle:vehiclesTransferedSuccessfully"),
      alertType: SUCCESS
    }

    yield call(action.callback, SUCCESS, afterRequestData)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_TRANSFER_FAILED" })

    const afterRequestData = {
      alertMessage: apiErrorHelpers.toString(errors),
      alertType: FAILURE
    }

    yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* transferVehicles

export function* loadVehicleGloveBox(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "VEHICLE_GLOVEBOX_UPDATE_REQUESTED" })
    const { vehicleId } = action.payload

    const request = {
      url: `/glovebox_items/?vehicle_id=${vehicleId}`
    }
    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_GLOVEBOX_UPDATE_SUCCEEDED",
      payload: {
        glovebox: response.body
      }
    })
    yield put({ type: "UNSET_LOADING" })
    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_GLOVEBOX_UPDATE_FAILED" })

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* saveDocument(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "SAVE_OR_UPDATE_UPLOADED_DOCUMENT_REQUESTED" })

    const { formData, documentItemId } = action.payload

    const request = {
      url: SAVE_DOCUMENTS_URL
    }
    if (documentItemId) {
      request.url = `${request.url}/${documentItemId}`
    }

    let baseUri = ""
    if (process.env.NODE_ENV === "production") {
      baseUri = window.location.origin
    } else {
      baseUri = api.apiUrl
    }
    const response = yield call(fetch, `${baseUri}${api.basePath}${request.url}`, {
      body: formData,
      method: documentItemId ? PUT : POST,
      headers: {
        Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
      }
    })
    if (!response.ok) {
      const responseData = yield call([response, "json"])
      throw responseData
    }
    const afterRequestData = {
      alertMessage: documentItemId
        ? i18n.t("vehicle:updateDocumentSuccessLabel")
        : i18n.t("vehicle:uploadDocumentSuccessLabel"),
      alertType: SUCCESS
    }
    yield put({
      type: "SAVE_OR_UPDATE_UPLOADED_DOCUMENT_SUCCEEDED",
      payload: afterRequestData
    })

    yield put({ type: "UNSET_LOADING" })
    yield call(action.callback, SUCCESS)
  } catch (errors) {
    var alertMessage = i18n.t("vehicle:uploadDocumentFailedLabel")
    if (errors.errors) {
      alertMessage += `: ${errors.errors}`
    }
    const afterRequestData = {
      alertMessage: alertMessage,
      alertType: FAILURE
    }
    yield call(action.callback, FAILURE)
    yield put({ type: "UNSET_LOADING" })
    yield put({
      type: "SAVE_OR_UPDATE_UPLOADED_DOCUMENT_FAILED",
      payload: afterRequestData
    })

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* deleteDocument(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "DELETE_UPLOADED_DOCUMENT_REQUESTED" })
    const { id } = action.payload

    const request = {
      url: `/glovebox_items/${id}`
    }

    const response = yield call(api.utility.del, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)
    const afterRequestData = {
      alertMessage: i18n.t("vehicle:uploadDocumentRemoveSuccessLabel"),
      alertType: SUCCESS
    }
    yield put({
      type: "DELETE_UPLOADED_DOCUMENT_SUCCEEDED",
      payload: afterRequestData
    })
    yield put({ type: "UNSET_LOADING" })
    yield call(action.callback, SUCCESS)
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    const afterRequestData = {
      alertMessage: i18n.t("vehicle:uploadDocumentRemoveFailedLabel"),
      alertType: FAILURE
    }
    yield call(action.callback, FAILURE)
    yield put({
      type: "VDELETE_UPLOADED_DOCUMENT_FAILED",
      payload: afterRequestData
    })
    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadVehicleGeotabBatteryLifeInfo(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_BATTERY_LIFE_INFO_REQUESTED" })
    const { vehicleId } = action.payload

    const request = {
      url: `/geotab_vehicles/${vehicleId}/battery_life_info`
    }
    const response = yield call(api.utility.get, api.path(request.url))

    yield put({
      type: "VEHICLE_GEOTAB_BATTERY_LIFE_INFO_REQUEST_SUCCEEDED",
      payload: {
        batteryLifeInfo: response.body
      }
    })

    apiErrorHelpers.catchErrors(response.body)

    yield put({ type: "UNSET_LOADING" })
    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({ type: "UNSET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_BATTERY_LIFE_INFO_REQUEST_FAILED" })
    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadVehicleGeotabEngineOilInfo(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_ENGINE_OIL_INFO_REQUESTED" })
    const { vehicleId } = action.payload

    const request = {
      url: `/geotab_vehicles/${vehicleId}/engine_oil_info`
    }
    const response = yield call(api.utility.get, api.path(request.url))

    yield put({
      type: "VEHICLE_GEOTAB_ENGINE_OIL_INFO_REQUEST_SUCCEEDED",
      payload: {
        engineOilLifeInfo: response.body
      }
    })

    apiErrorHelpers.catchErrors(response.body)

    yield put({ type: "UNSET_LOADING" })
    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({ type: "UNSET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_ENGINE_OIL_INFO_REQUEST_FAILED" })

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadVehicleGeotabTirePressureInfo(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_TIRE_PRESSURE_INFO_REQUESTED" })
    const { vehicleId } = action.payload

    yield put({
      type: "VEHICLE_GEOTAB_TIRE_PRESSURE_INFO_REQUEST_SUCCEEDED",
      payload: {
        tirePressureInfo: {}
      }
    })

    const request = {
      url: `/geotab_vehicles/${vehicleId}/tire_pressure_info`
    }
    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_GEOTAB_TIRE_PRESSURE_INFO_REQUEST_SUCCEEDED",
      payload: {
        tirePressureInfo: response.body
      }
    })
    yield put({ type: "UNSET_LOADING" })
    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({ type: "UNSET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_TIRE_PRESSURE_INFO_REQUEST_FAILED" })

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadVehicleGeotabFaultDataInfo(action) {
  try {
    yield put({ type: "SET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_FAULT_CODES_INFO_REQUESTED" })
    const { vehicleId } = action.payload

    yield put({
      type: "VEHICLE_GEOTAB_FAULT_CODES_INFO_REQUEST_SUCCEEDED",
      payload: {
        faultCodesInfo: []
      }
    })

    const request = {
      url: `/geotab_vehicles/${vehicleId}/fault_data`
    }
    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: "VEHICLE_GEOTAB_FAULT_CODES_INFO_REQUEST_SUCCEEDED",
      payload: {
        faultCodesInfo: response.body
      }
    })
    yield put({ type: "UNSET_LOADING" })
    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({ type: "UNSET_LOADING" })
    yield put({ type: "VEHICLE_GEOTAB_FAULT_CODES_INFO_REQUEST_FAILED" })

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* pullOemMaintenanceSchedules(action) {
  try {
    yield put({ type: "PULL_OEM_MAINTENANCE_SCHEDULES_REQUESTED" })

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const { vehicle } = action.payload

    const request = {
      url: `${VEHICLE_MAINTENANCE_SCHEDULES}/oem`,
      body: {
        vehicleId: vehicle.id,
        interval: vehicle.miles
      }
    }

    const response = yield call(api.utility.get, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    if (response.body) {
      const services = response.body.intervals || []
      yield put({
        type: "PULL_OEM_MAINTENANCE_SCHEDULES_SUCCEEDED",
        payload: {
          id: vehicle.id,
          oemServices: services.filter((service) => service)
        }
      })
    }

    if (action.callback) {
      yield call(action.callback, SUCCESS)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "PULL_OEM_MAINTENANCE_SCHEDULES_FAILED" })

    yield call(action.callback, FAILURE)

    apiErrorHelpers.consoleLog(errors)
  }
} // function* pullOemMaintenanceSchedules

export function* getVehicleData(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const vehicleId = action.payload.id
    let body = {}

    let url = `/vehicles/${vehicleId}`
    const request = {
      url: url,
      body: body
    }

    const response = yield call(api.utility.get, api.pathV6(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    if (action.callback) {
      yield call(action.callback, response.body)
    }
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_LOAD_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    if (action.callback) yield call(action.callback, FAILURE)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadAllVehicles(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    yield put({ type: "VEHICLES_LOAD_REQUESTED" })

    const vehiclesRequest = {
      url: LOAD_ALL_VEHICLES_URL,
      body: {
        request: "active_order"
      }
    }

    const vehiclesResponse = yield call(api.utility.get, api.path(vehiclesRequest.url), {
      body: vehiclesRequest.body
    })

    apiErrorHelpers.catchErrors(vehiclesResponse.body)

    yield put({
      type: "VEHICLES_LOAD_SUCCEEDED",
      payload: {
        vehicles: vehiclesResponse.body
      }
    })
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_LOAD_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    if (action.callback) yield call(action.callback, FAILURE)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* loadVehicleStateSummary(action) {
  try {
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    yield put({ type: "VEHICLES_LOAD_REQUESTED" })

    const vehiclesRequest = {
      url: LOAD_VEHICLE_STATE_SUMMARY
    }

    const vehiclesResponse = yield call(api.utility.get, api.pathV6(vehiclesRequest.url), {
      body: vehiclesRequest.body
    })

    apiErrorHelpers.catchErrors(vehiclesResponse.body)

    const defaultValue = {
      past_due_vehicle_ids: [],
      currently_due_vehicle_ids: [],
      vehicles: []
    }

    yield put({
      type: "VEHICLES_STATE_SUMMARY_LOAD_SUCCEEDED",
      payload: vehiclesResponse.body ?? defaultValue
    })
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: "VEHICLE_LOAD_FAILED" })
    yield put({ type: "SET_LOADING_ERROR" })
    yield put({ type: "UNSET_LOADING" })

    if (action.callback) yield call(action.callback, FAILURE)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* bulkActivateDeactivateVehicle(action) {
  try {
    const { status, vehicleIds } = action.payload
    const vehiclesRequest = {
      url: `${BULK_VEHICLE_URL}/${
        status === DEACTIVE ? "deactivate_multiple" : "activate_multiple"
      }`
    }

    const vehiclesResponse = yield call(api.utility.put, api.pathV6(vehiclesRequest.url), {
      method: PUT,
      body: {
        vehicle_ids: vehicleIds || []
      },
      headers: {
        Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
      }
    })

    if (action.callback) {
      yield call(action.callback, vehiclesResponse.body)
    }

    apiErrorHelpers.catchErrors(vehiclesResponse.body)
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    if (action.callback) yield call(action.callback, FAILURE)
    apiErrorHelpers.consoleLog(errors)
  }
}

export function* updateFleetcorVehicle(action) {
  try {
    const { enroll_code, vehicleId } = action.payload

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })
    let currentFleet = JSON.parse(sessionStorage.getItem(CURRENT_FLEET))

    const request = {
      url: `/fleet/${currentFleet.id}/fleetcor_vehicles/${vehicleId}`,
      body: { enroll_code: enroll_code }
    }

    const response = yield call(api.utility.patch, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    if (action.callback) yield call(action.callback, SUCCESS, response.body)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    if (action.callback) yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* searchVehicleByVin(action) {
  try {
    const { vin, cache } = action.payload

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: `/vehicles/search?search[vin]=${vin}&search[cache]=${cache}`
    }

    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    if (action.callback) yield call(action.callback, SUCCESS, response.body)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    if (action.callback) yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* licensePlateToVin(action) {
  try {
    const { license, selectedState } = action.payload

    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })

    const request = {
      url: `/vehicle_plate/convert_plate?license_plate_number=${license}&license_plate_state=${selectedState}`
    }

    const response = yield call(api.utility.get, api.path(request.url))

    apiErrorHelpers.catchErrors(response.body)

    if (action.callback) yield call(action.callback, SUCCESS, response.body)
  } catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    const afterRequestData = {
      error: apiErrorHelpers.toString(errors)
    }

    if (action.callback) yield call(action.callback, FAILURE, afterRequestData)

    apiErrorHelpers.consoleLog(errors)
  }
}

export function* vehicleReassignment(action) {
  try {
    yield put({ type: "VEHICLE_REASSIGNMENT_REQUESTED" })
    api.setAuthApi({
      Authorization: sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
    })
    const request = { url: VEHICLE_REASSIGNMENT_URL, body: action.payload }
    const response = yield call(api.utility.put, api.pathV6(request.url), { body: request.body })
    apiErrorHelpers.catchErrors(response.body)
    yield put({ type: "VEHICLE_REASSIGNMENT_SUCCEEDED" })
    if (action.callback) yield call(action.callback, SUCCESS)
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    yield put({ type: "VEHICLE_REASSIGNMENT_FAILED" })
    if (action.callback) yield call(action.callback, FAILURE)
    apiErrorHelpers.consoleLog(errors)
  }
} // function* vehicleReassignment

export default function* vehicleSaga() {
  yield takeEvery("VEHICLE_CREATE_OR_UPDATE_SAGA", createOrUpdateVehicle)
  yield takeEvery("VEHICLE_CREATE_OR_UPDATE_SAGA_V6", createOrUpdateVehicleV6)
  yield takeEvery("VEHICLE_DELETE_SAGA", deleteVehicle)
  yield takeEvery("VEHICLE_LOAD_SAGA", loadVehicle)
  yield takeEvery("VEHICLE_UPDATE_SAGA", updateVehicle)
  yield takeEvery("VEHICLE_BULK_UPSERT_SAGA", bulkUpsertVehicles)
  yield takeEvery("VEHICLES_LOAD_SAGA", loadVehicles)
  yield takeEvery("VEHICLES_BASIC_LOAD_SAGA", loadBasicVehicles)
  yield takeEvery("YEARS_LOAD_SAGA", loadDropdown.bind(this, YEARS, getYearsUrl))
  yield takeEvery("MAKES_LOAD_SAGA", loadDropdown.bind(this, MAKES, getMakesUrl))
  yield takeEvery("MODELS_LOAD_SAGA", loadDropdown.bind(this, MODELS, getModelsUrl))
  yield takeEvery("ENGINES_LOAD_SAGA", loadDropdown.bind(this, ENGINES, getEnginesUrl))
  yield takeEvery("VEHICLE_DEACTIVATE_SAGA", deactivateVehicle)
  yield takeEvery("VEHICLE_TRANSFER_SAGA", transferVehicles)
  yield takeEvery("VEHICLE_LOAD_SERVICES_SAGA", fetchVehicleServices)
  yield takeEvery("VEHICLE_GLOVEBOX_SAGA", loadVehicleGloveBox)
  yield takeEvery("SAVE_OR_UPDATE_UPLOADED_DOCUMENT", saveDocument)
  yield takeEvery("DELETE_UPLOADED_DOCUMENT", deleteDocument)
  yield takeEvery("VEHICLE_GEOTAB_ENGINE_OIL_INFO_LOAD_SAGA", loadVehicleGeotabEngineOilInfo)
  yield takeEvery("VEHICLE_GEOTAB_BATTERY_LIFE_INFO_LOAD_SAGA", loadVehicleGeotabBatteryLifeInfo)
  yield takeEvery("VEHICLE_GEOTAB_TIRE_PRESSURE_INFO_LOAD_SAGA", loadVehicleGeotabTirePressureInfo)
  yield takeEvery("VEHICLE_GEOTAB_FAULT_CODES__INFO_LOAD_SAGA", loadVehicleGeotabFaultDataInfo)
  yield takeEvery("PULL_OEM_MAINTENANCE_SCHEDULES", pullOemMaintenanceSchedules)
  yield takeEvery("GET_VEHICLE_DATA", getVehicleData)
  yield takeEvery("LOAD_VEHICLES", loadAllVehicles)
  yield takeEvery("LOAD_VEHICLE_STATE_SUMMARY", loadVehicleStateSummary)
  yield takeEvery("BULK_ACTIVATE_DEACTIVATE_VEHICLE", bulkActivateDeactivateVehicle)
  yield takeEvery("UPDATE_FLEETCOR_VEHICLE", updateFleetcorVehicle)
  yield takeEvery("SEARCH_VEHICLE_BY_VIN", searchVehicleByVin)
  yield takeEvery("LICENSE_PLATE_TO_VIN", licensePlateToVin)
  yield takeEvery("VEHICLE_REASSIGNMENT_SAGA", vehicleReassignment)
}
